import { useEffect, useState } from 'react';

export type TimeOfDay = 'morning' | 'day' | 'evening';

const DAY_START_HOUR = 12;
const EVENING_START_HOUR = 18;

export const getTimeOfDay = (date: Date = new Date()): TimeOfDay => {
  const hour = date.getHours();
  if (hour < DAY_START_HOUR) return 'morning';
  if (hour < EVENING_START_HOUR) return 'day';
  return 'evening';
};

export const getGreeting = (date: Date = new Date()): string => {
  switch (getTimeOfDay(date)) {
    case 'morning':
      return 'Good morning';
    case 'day':
      return 'Good afternoon';
    default:
      return 'Good evening';
  }
};

/** Milliseconds until the next Morning / Day / Evening boundary. */
const msToNextBand = (now: Date): number => {
  const hour = now.getHours();
  const nextHour =
    hour < DAY_START_HOUR
      ? DAY_START_HOUR
      : hour < EVENING_START_HOUR
        ? EVENING_START_HOUR
        : 24;
  const next = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    nextHour,
  );
  return next.getTime() - now.getTime() + 1000;
};

/** Current time-of-day band, re-evaluated when the band rolls over. */
export const useTimeOfDay = (): TimeOfDay => {
  const [band, setBand] = useState<TimeOfDay>(() => getTimeOfDay());
  useEffect(() => {
    const tick = () => setBand(getTimeOfDay());
    let timer = window.setTimeout(function roll() {
      tick();
      timer = window.setTimeout(roll, msToNextBand(new Date()));
    }, msToNextBand(new Date()));
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  return band;
};
